import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Skeleton,
  Tooltip,
} from '@patternfly/react-core';
import { PlusCircleIcon, ShieldAltIcon, SyncAltIcon } from '@patternfly/react-icons';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { VirtualMachine } from '../../../types/models/VirtualMachine';
import { useApiFetch } from '../../../api';
import { useRoles } from '../../../contexts/roles/RolesContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useTenantCatalog } from '../../../hooks/useTenantCatalog';
import { useVmBackups } from '../../../hooks/useVmBackups';
import { useAdminSpecials } from '../../../hooks/useAdminSpecials';
import { useClientSpecials } from '../../../hooks/useClientSpecials';
import { formatPriceAmount, formatUnitPrice } from '../../../utils/money';
import type { BackupVmTotals } from '../../../types/models/Backup';
import type { ScalarPricing } from '../../../types/models/TenantCatalogProduct';
import { SPECIAL_DEAL_PARENT_VM } from '../../../types/models/SpecialDeal';
import type { AdminSpecialDeal } from '../../../types/models/SpecialDeal';
import type { ApiResponseAdminSpecialUpdate } from '../../../types/api/ApiResponseSpecialDeal';
import InlineEditSelect from '../../../components/InlineEditSelect/InlineEditSelect';
import SpecialDealModal from '../../Organization/Agreements/SpecialDealModal';
import {
  formatDealValue,
  formatOccurrencesLeft,
  renderDealKindBadge,
} from '../../Organization/Agreements/specialDealDisplay';
import renderPowerStateLabel from './powerStateLabel';
import VmBillingCard from './VmBillingCard';

interface VmOverviewTabProps {
  vm: VirtualMachine;
  powerState: string | null;
  powerStateLoading: boolean;
  onVmUpdate: () => void;
}

const replicaStatusText = (status: string | null): string => {
  if (status === 'active') {
    return 'Running on backup';
  }
  if (status === 'offline') {
    return 'Standby';
  }
  return status ?? '\u2014';
};

// Quantities (vCPU counts, GB) rendered fr-BE like every other number in the app. Up to 4 fraction
// digits so the displayed allocation is exactly the billed quantity (the meter emits 4-decimal
// quantities) — a 2-digit display of e.g. 1500 MB ("1,46 GB") would visibly contradict the row's
// allocation × unit-price = cost math.
const QUANTITY_FORMATTER = new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 4 });

// The meter rounds every billed quantity to 4 decimals (number_format(·, 4)); mirror it so the
// displayed cost matches the invoice to the cent.
const quantize = (value: number): number => Math.round(value * 10000) / 10000;

// A byte count converts to the STORAGE product's GB at 10^9, NOT 2^30: that product is priced per
// *decimal* GB (the meter reads NetBox's disk MB and divides by 1000 \u2014 "100000 means 100 GB"), so
// pricing backup bytes as GiB would undercharge the same space by ~7% and make the two
// STORAGE-priced lines of this table silently incomparable.
const BYTES_PER_GB = 1_000_000_000;

// One priced line of the Resources table \u2014 a billed VM component per the backend
// VirtualMachineMeter (product keys VCPU / VRAM / STORAGE), plus the backup-storage line, which is
// priced by the same STORAGE product as the base disks.
interface VmResourceRow {
  /** React key. Distinct from `productKey`: Disk and Backup storage are both priced by STORAGE. */
  key: string;
  productKey: 'VCPU' | 'VRAM' | 'STORAGE';
  label: string;
  /** Billed quantity in the product's unit, or null when the allocation is not set in NetBox. */
  quantity: number | null;
  allocationText: string;
  /** Tooltip on the allocation cell, for a figure that needs a word of provenance. */
  allocationHint?: string;
  /** Unit the product is priced by, shown after the unit price (e.g. "10,00 \u20ac / vCPU"). */
  unitLabel: string;
  /**
   * A zero quantity is a legitimate \u20ac0 line rather than the meter's "incomplete data" skip. True
   * only for backup storage: a VM whose backups occupy nothing really does cost nothing for them,
   * whereas a NetBox VM with 0 vCPUs is unbilled data entry to fix.
   */
  billsAtZero?: boolean;
}

// One ACTIVE Special deal attached to this VM, unified across the two read surfaces (staff read
// the admin Deal Object — kept on `deal` for the management affordances; clients the tenant
// projection, which has no admin payload).
interface VmDealRow {
  id: number;
  kind: string;
  label: string;
  value: string;
  occurrencesLeft: number | null;
  deal?: AdminSpecialDeal;
}

// The billed quantity conversions mirror the backend VirtualMachineMeter exactly: vCPUs verbatim,
// VRAM at 1024 MB/GB (memory sizes are binary), STORAGE at 1000 MB/GB (the NetBox data-entry
// convention \u2014 NetBox's disk field is MB, so 100000 means 100 GB).
const buildResourceRows = (vm: VirtualMachine): VmResourceRow[] => {
  const vcpus = vm.vcpus !== null ? quantize(Number(vm.vcpus)) : null;
  const memoryGb = vm.memory !== null ? quantize(vm.memory / 1024) : null;
  const diskGb = vm.disk !== null ? quantize(vm.disk / 1000) : null;
  return [
    {
      key: 'VCPU',
      productKey: 'VCPU',
      label: 'vCPUs',
      quantity: vcpus,
      allocationText: vcpus !== null ? `${QUANTITY_FORMATTER.format(vcpus)} vCPU${vcpus === 1 ? '' : 's'}` : 'Not set',
      unitLabel: 'vCPU',
    },
    {
      key: 'VRAM',
      productKey: 'VRAM',
      label: 'Memory',
      quantity: memoryGb,
      allocationText: memoryGb !== null ? `${QUANTITY_FORMATTER.format(memoryGb)} GB` : 'Not set',
      unitLabel: 'GB',
    },
    {
      key: 'STORAGE',
      productKey: 'STORAGE',
      label: 'Disk',
      quantity: diskGb,
      allocationText: diskGb !== null ? `${QUANTITY_FORMATTER.format(diskGb)} GB` : 'Not set',
      unitLabel: 'GB',
    },
  ];
};

/**
 * The "Backup storage" line, appended under "Disk" and priced by the same STORAGE product.
 *
 * BILLING RULE (settled with Bruno 2026-07-29 — do not "simplify" this to `allocated_bytes`):
 * a backup subject is a **full copy of the disk plus its increments**, and it bills as
 *
 *     max(base at provisioned size + increments at allocated size, the VM's provisioned disk)
 *
 * so a 50 GB VM with six 1 GB increments bills 56 GB of backup — on top of the 50 GB the Disk row
 * already bills. The 50 GB is charged twice **on purpose**: once as the disk the VM runs on, once
 * as the copy occupying the backup server. The floor is what "never go below the initial disk
 * size" means: thin provisioning must not shrink the charge, so a sparse 50 GB base that allocates
 * only 8 GB of real blocks still bills 50 GB.
 *
 * `size_bytes` is the practical stand-in for "base at provisioned size + increments": a qcow2
 * base's apparent file length tracks the provisioned disk even when its blocks are punched out,
 * which is exactly the sparse behaviour the floor exists to defeat. `allocated_bytes` is therefore
 * NOT the billed figure — it is shown as context only.
 *
 * INTERIM. The authoritative figure belongs in `BackupUsage::forVm()`, the primitive the per-VM
 * backup invoice line will meter; until it publishes one, this derives the same rule from the two
 * sums it does publish. That approximation differs from the exact rule only where an *increment*
 * is itself sparse (apparent > allocated). Replace this with the backend's field when it lands.
 *
 * `totals` covers present subjects only and ignores the `include_missing=1` the list is fetched
 * with, so it is never the sum of the listed rows.
 *
 * Returns `null` when the VM has no visible backup inventory, so the row is simply absent rather
 * than a zero line on the (many) VMs that are not backed up.
 */
export const buildBackupStorageRow = (
  totals: BackupVmTotals | null,
  provisionedDiskGb: number | null,
): VmResourceRow | null => {
  if (totals === null || totals.subject_count <= 0) {
    return null;
  }
  const apparentGb = totals.size_bytes / BYTES_PER_GB;
  const allocatedGb = quantize(totals.allocated_bytes / BYTES_PER_GB);
  const billableGb = quantize(Math.max(apparentGb, provisionedDiskGb ?? 0));
  const copies = `${totals.subject_count} backup ${totals.subject_count === 1 ? 'copy' : 'copies'}`;
  return {
    key: 'BACKUP_STORAGE',
    productKey: 'STORAGE',
    label: 'Backup storage',
    quantity: billableGb,
    allocationText: `${QUANTITY_FORMATTER.format(billableGb)} GB`,
    allocationHint:
      `A full copy of the disk plus its increments, across ${copies}` +
      (provisionedDiskGb !== null
        ? `, never billed below the ${QUANTITY_FORMATTER.format(provisionedDiskGb)} GB provisioned on the VM`
        : '') +
      `. The backup server actually allocates ${QUANTITY_FORMATTER.format(allocatedGb)} GB of it — ` +
      'thin provisioning does not reduce the charge. Decimal GB, matching the disk rate.',
    unitLabel: 'GB',
    billsAtZero: true,
  };
};

const VmOverviewTab: React.FunctionComponent<VmOverviewTabProps> = ({
  vm,
  powerState,
  powerStateLoading,
  onVmUpdate,
}) => {
  const apiFetch = useApiFetch();
  const { roles } = useRoles();
  const { addNotification, setDrawerOpen } = useNotifications();
  const user = useUserContext();
  // Effective per-client prices for the billed VM components (VCPU / VRAM / STORAGE) come from the
  // read-only customer catalog — the same source as the Organization Prices page, so a negotiated
  // override shows the client's real price here too.
  const {
    products: catalogProducts,
    loading: pricesLoading,
    error: pricesError,
    refetch: refetchPrices,
  } = useTenantCatalog();
  // The VM's backup footprint comes from its own endpoint (GET /datacenters/vms/{id}/backups →
  // `totals`), which is allowed here because VirtualMachineDetail mounts its tabs with
  // `mountOnEnter` — and Overview is the tab that needs the figure, since backup storage is part
  // of the recurring cost this card totals.
  const {
    totals: backupTotals,
    loading: backupsLoading,
    error: backupsError,
    refetch: refetchBackups,
  } = useVmBackups(vm.id ?? null);

  const { target } = useImpersonation();
  const isStaff = user?.is_ancalagon_staff === true;
  // The billed organization for deal management: the impersonation target when staff acts for a
  // tenant, else the user's own org. The /admin/billing/specials API takes this id in the BODY.
  const dealClientId = target?.id ?? user?.clientID ?? null;
  // Attached Special deals on this VM's invoice. Staff read the admin list (its Deal Objects carry
  // billed_event_count etc., which the edit modal's mutability rules need); clients read the tenant
  // projection — exactly one of the two fetches runs. Allowed to fetch here for the same reason as
  // the catalog/backups: the Overview tab mounts with mountOnEnter, and Refresh re-runs it.
  const {
    specials: adminSpecials,
    loading: adminSpecialsLoading,
    error: adminSpecialsError,
    refetch: refetchAdminSpecials,
  } = useAdminSpecials(dealClientId, isStaff);
  const {
    specials: clientSpecials,
    loading: clientSpecialsLoading,
    error: clientSpecialsError,
    refetch: refetchClientSpecials,
  } = useClientSpecials(!isStaff);
  const specialsLoading = isStaff ? adminSpecialsLoading : clientSpecialsLoading;
  const specialsError = isStaff ? adminSpecialsError : clientSpecialsError;
  const refetchSpecials = isStaff ? refetchAdminSpecials : refetchClientSpecials;

  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [editDeal, setEditDeal] = useState<AdminSpecialDeal | null>(null);

  // Only ACTIVE deals attached to THIS VM render on the card (the tenant read returns active rows
  // only; the admin list is filtered the same way so both variants show the same set).
  const vmDealRows = useMemo<VmDealRow[]>(() => {
    if (vm.id === null) {
      return [];
    }
    if (isStaff) {
      return adminSpecials
        .filter((d) => d.parent_type === SPECIAL_DEAL_PARENT_VM && d.parent_id === vm.id && d.active)
        .map((d) => ({
          id: d.id,
          kind: d.kind,
          label: d.product.label,
          value: d.value,
          occurrencesLeft: d.occurrences_left,
          deal: d,
        }));
    }
    return clientSpecials
      .filter((s) => s.parent_type === SPECIAL_DEAL_PARENT_VM && s.parent_id === vm.id)
      .map((s) => ({
        id: s.id,
        kind: s.kind,
        label: s.product.label ?? '—',
        value: s.value,
        occurrencesLeft: s.occurrences_left,
      }));
  }, [isStaff, adminSpecials, clientSpecials, vm.id]);

  // The API rejects deals on unenrolled VMs — the "+" mirrors that instead of offering a dead end.
  const canAddDeal = vm.billing?.enrolled === true && vm.id !== null && dealClientId !== null;

  const handleDealDeactivate = useCallback(
    async (deal: AdminSpecialDeal) => {
      try {
        const response = await apiFetch(`/admin/billing/specials/${deal.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ active: false }),
        });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseAdminSpecialUpdate | null;
        if (body?.status === 'success' && body.data?.special) {
          addNotification({
            title: 'Agreement deactivated',
            srTitle: 'Success alert',
            description: `${deal.product.label} no longer emits invoice lines on ${vm.name}. An already-emitted pending line still lands on its invoice.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          void refetchAdminSpecials();
        } else {
          addNotification({
            title: 'Could not deactivate agreement',
            srTitle: 'Error alert',
            description: body?.data?.active ?? body?.data?.body ?? body?.message ?? 'Could not deactivate the agreement.',
            variant: 'danger',
          });
          setDrawerOpen(true);
        }
      } catch (err) {
        addNotification({
          title: 'Could not deactivate agreement',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    },
    [apiFetch, addNotification, setDrawerOpen, refetchAdminSpecials, vm.name],
  );

  const dealRowActions = (deal: AdminSpecialDeal): IAction[] => [
    {
      title: 'Modify',
      onClick: () => setEditDeal(deal),
    },
    {
      title: 'Deactivate',
      onClick: () => void handleDealDeactivate(deal),
    },
  ];

  const roleOptions = roles.filter((r) => r.id !== null).map((r) => ({ value: r.id as number, label: r.name }));

  const scalarPricingByKey = useMemo(() => {
    const map = new Map<string, ScalarPricing>();
    catalogProducts.forEach((p) => {
      if (p.pricing.model === 'scalar') {
        map.set(p.product_key, p.pricing);
      }
    });
    return map;
  }, [catalogProducts]);

  const resourceRows = useMemo(() => {
    const rows = buildResourceRows(vm);
    // The backup line is floored at the VM's provisioned disk, so it needs the same MB/1000 the
    // Disk row bills on — read it back off that row rather than converting twice.
    const provisionedDiskGb = rows.find((r) => r.key === 'STORAGE')?.quantity ?? null;
    const backupRow = buildBackupStorageRow(backupTotals, provisionedDiskGb);
    return backupRow === null ? rows : [...rows, backupRow];
  }, [vm, backupTotals]);

  // Display-only float math (money stays a DECIMAL string end-to-end everywhere else): a line's
  // monthly cost is quantity × unit price, null when either side is unknown (allocation not set,
  // product missing from the catalog, or unpriced). A zero allocation is also null: the meter
  // refuses to bill on vcpus/memory/disk <= 0 ("incomplete data"), so pricing it as €0 would show
  // a confident total for a VM the backend would not bill at all. Backup storage is the one line
  // exempt from that rule (billsAtZero) — an empty backup footprint is a real €0, not bad data.
  const lineCost = (row: VmResourceRow): number | null => {
    const pricing = scalarPricingByKey.get(row.productKey);
    if (row.quantity === null || pricing === undefined || pricing.unit_price === null) {
      return null;
    }
    if (row.quantity < 0 || (row.quantity === 0 && row.billsAtZero !== true)) {
      return null;
    }
    const unitPrice = parseFloat(pricing.unit_price);
    return Number.isNaN(unitPrice) ? null : row.quantity * unitPrice;
  };

  const lineCosts = resourceRows.map(lineCost);
  // Mirror the billing meter's fail-loud posture: a total with a missing component would silently
  // understate the real recurring cost, so it only renders when every line is computable.
  const totalCost = lineCosts.every((cost) => cost !== null)
    ? (lineCosts as number[]).reduce((sum, cost) => sum + cost, 0)
    : null;
  // The invoice-preview total, rendered BELOW the special agreements so it matches what the next
  // invoice will actually charge. It mirrors the backend's settlement algorithm (special-products
  // design, decisions 6/10): attached charge deals join the positive base, a percent rebate is
  // computed on that base, and every discount — fixed and percent alike — is applied in
  // assignment-id order, clamped to the remaining amount, so the total never nets negative.
  // Withheld (null) when the resource total is incomputable or the agreements list failed to load:
  // a total silently missing deal lines would contradict the invoice it claims to preview.
  const invoiceTotal = (() => {
    if (totalCost === null || specialsError !== null) {
      return null;
    }
    let base = totalCost;
    for (const row of vmDealRows) {
      if (row.kind === 'charge') {
        const v = parseFloat(row.value);
        if (Number.isNaN(v)) {
          return null;
        }
        base += v;
      }
    }
    let remaining = base;
    const discounts = vmDealRows.filter((r) => r.kind !== 'charge').sort((a, b) => a.id - b.id);
    for (const row of discounts) {
      const v = parseFloat(row.value);
      if (Number.isNaN(v)) {
        return null;
      }
      const nominal = row.kind === 'discount_percent' ? (base * v) / 100 : v;
      remaining -= Math.min(nominal, remaining);
    }
    return remaining;
  })();
  // The backup line and the deal lines are part of the total, so it must not render a partial
  // figure while either is still in flight — it would visibly change a moment later.
  const totalLoading = pricesLoading || backupsLoading || specialsLoading;
  // A footprint that never loaded leaves the total silently understated; one that failed to
  // *refresh* leaves a stale figure priced into it — the hook keeps the last good `totals` on
  // error. Both are the silent understatement this card's fail-loud posture forbids, so each gets
  // its own wording, the same warm-vs-cold split the pricing note below makes.
  let backupUsageNote: string | null = null;
  if (!backupsLoading && vm.id !== null && backupTotals === null) {
    backupUsageNote =
      `Backup storage could not be loaded${backupsError ? `: ${backupsError.message}` : ''}` +
      ' — the total excludes it.';
  } else if (!backupsLoading && backupsError !== null) {
    backupUsageNote =
      `Backup storage could not be refreshed: ${backupsError.message}` +
      ' — the line above may be out of date.';
  }

  const handleRoleSave = useCallback(
    async (newValue: string | number | null) => {
      try {
        const response = await apiFetch(`/datacenters/vms/${vm.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ role: newValue }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.status !== 'success') {
          throw new Error('Unexpected response from server');
        }

        addNotification({
          title: 'Role updated',
          srTitle: 'Success alert',
          description: `Role for ${vm.name} has been updated.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onVmUpdate();
      } catch (err) {
        addNotification({
          title: 'Failed to update role',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        throw err;
      }
    },
    [apiFetch, vm.id, vm.name, addNotification, setDrawerOpen, onVmUpdate],
  );

  return (
    <Grid hasGutter md={6} style={{ marginTop: '1rem' }}>
      <GridItem>
        <Card isFullHeight>
          <CardTitle>General Information</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Status</DescriptionListTerm>
                <DescriptionListDescription>
                  {renderPowerStateLabel(powerState, powerStateLoading)}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Role</DescriptionListTerm>
                <DescriptionListDescription>
                  <InlineEditSelect
                    currentLabel={vm.role?.name ?? null}
                    currentValue={vm.role?.id ?? null}
                    options={roleOptions}
                    onSave={handleRoleSave}
                    placeholder="Not assigned"
                    ariaLabel="Edit VM role"
                    clearable
                    clearLabel="No role"
                  />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Platform</DescriptionListTerm>
                <DescriptionListDescription>{vm.platform?.name ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Description</DescriptionListTerm>
                <DescriptionListDescription>{vm.description || '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Comments</DescriptionListTerm>
                <DescriptionListDescription>{vm.comments || '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Libvirt UUID</DescriptionListTerm>
                <DescriptionListDescription>
                  {vm.libvirt_uuid ? (
                    <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{vm.libvirt_uuid}</span>
                  ) : (
                    '\u2014'
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem>
        <Card isFullHeight>
          {/* The Overview tab is mounted once (mountOnEnter, no unmountOnExit), so without this
              Refresh the prices and backup footprint fetched on first open would be frozen for the
              whole page visit — including after a failed load. */}
          <CardHeader
            actions={{
              actions: (
                <>
                  {/* Staff-only (the real user's flag alone, like VmBillingCard): opens the deal
                      modal locked to this VM. isAriaDisabled (not isDisabled) keeps the disabled
                      button focusable so its "enroll first" tooltip can show. */}
                  {isStaff && (
                    <Tooltip
                      content={
                        canAddDeal
                          ? 'Add a special agreement to this virtual machine'
                          : 'Enroll the VM for monthly billing first'
                      }
                    >
                      <Button
                        variant="plain"
                        aria-label="Add special agreement"
                        icon={<PlusCircleIcon />}
                        isAriaDisabled={!canAddDeal}
                        onClick={() => setIsDealModalOpen(true)}
                      />
                    </Tooltip>
                  )}
                  <Button
                    variant="plain"
                    aria-label="Refresh pricing, backup usage and special agreements"
                    icon={<SyncAltIcon />}
                    onClick={() => {
                      void refetchPrices();
                      void refetchBackups();
                      void refetchSpecials();
                    }}
                    isDisabled={pricesLoading || backupsLoading}
                  />
                </>
              ),
            }}
          >
            <CardTitle>Resources</CardTitle>
          </CardHeader>
          <CardBody>
            <Table aria-label="Virtual machine resources and monthly pricing" variant="compact">
              <Thead>
                <Tr>
                  <Th>Resource</Th>
                  <Th>Allocation</Th>
                  <Th>Unit price</Th>
                  <Th>Monthly cost</Th>
                </Tr>
              </Thead>
              <Tbody>
                {resourceRows.map((row, index) => {
                  const pricing = scalarPricingByKey.get(row.productKey);
                  const cost = lineCosts[index];
                  return (
                    <Tr key={row.key}>
                      <Td dataLabel="Resource">{row.label}</Td>
                      <Td dataLabel="Allocation">
                        {row.allocationHint ? (
                          <Tooltip content={row.allocationHint}>
                            <span tabIndex={0}>{row.allocationText}</span>
                          </Tooltip>
                        ) : (
                          row.allocationText
                        )}
                      </Td>
                      <Td dataLabel="Unit price">
                        {pricesLoading ? (
                          <Skeleton
                            width="70%"
                            fontSize="sm"
                            screenreaderText={index === 0 ? 'Loading resource prices' : undefined}
                          />
                        ) : pricing === undefined || pricing.unit_price === null ? (
                          // "On request" is only true for a successfully loaded catalog — after a
                          // failed fetch the honest message is that the price didn't load.
                          <Tooltip content={pricesError ? 'Pricing could not be loaded' : 'Price available on request'}>
                            <span tabIndex={0}>
                              <Content component="small">—</Content>
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                            {`${formatUnitPrice(pricing.unit_price)} / ${row.unitLabel}`}
                            {pricing.is_custom && (
                              <Label isCompact color="blue" className="pf-v6-u-ml-xs">
                                Custom
                              </Label>
                            )}
                          </>
                        )}
                      </Td>
                      <Td dataLabel="Monthly cost">
                        {pricesLoading ? (
                          <Skeleton width="50%" fontSize="sm" />
                        ) : cost !== null ? (
                          formatPriceAmount(cost)
                        ) : (
                          '—'
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
            {vmDealRows.length > 0 && (
              <>
                <Content component="h4" className="pf-v6-u-mt-md">
                  Special agreements
                </Content>
                <Table aria-label="Special agreements billed on this virtual machine's invoice" variant="compact">
                  <Tbody>
                    {vmDealRows.map((row) => (
                      <Tr key={row.id}>
                        <Td dataLabel="Kind">{renderDealKindBadge(row.kind)}</Td>
                        <Td dataLabel="Agreement">{row.label}</Td>
                        <Td dataLabel="Value">{formatDealValue(row.kind, row.value)}</Td>
                        <Td dataLabel="Remaining">
                          {row.occurrencesLeft !== null && (
                            <Label isCompact color="grey">
                              {formatOccurrencesLeft(row.occurrencesLeft)} left
                            </Label>
                          )}
                        </Td>
                        {isStaff && row.deal !== undefined && (
                          <Td isActionCell>
                            <ActionsColumn items={dealRowActions(row.deal)} />
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Content component="small">
                  Agreement lines are settled at invoicing time: a percent rebate is computed on the invoiced
                  base, and every rebate — fixed or percent — can be reduced (clamped) so the invoice never
                  nets negative.
                </Content>
              </>
            )}
            {specialsError !== null && (
              <Content component="small">
                Special agreements could not be loaded: {specialsError} — the total is withheld (it would
                not match the invoice without them).
              </Content>
            )}
            {/* The total sits BELOW the agreement lines so it is the figure the next invoice will
                actually charge: resources + charges, minus the clamped rebates. */}
            <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} className="pf-v6-u-mt-md">
              <FlexItem>
                <strong>Total recurring cost (excl. VAT)</strong>
              </FlexItem>
              <FlexItem>
                {totalLoading ? (
                  <Skeleton width="90px" fontSize="sm" screenreaderText="Loading the recurring cost total" />
                ) : (
                  <strong>{invoiceTotal !== null ? `${formatPriceAmount(invoiceTotal)} / month` : '—'}</strong>
                )}
              </FlexItem>
            </Flex>
            {vmDealRows.some((r) => r.occurrencesLeft !== null) && (
              <Content component="small">
                The total includes limited agreements only while their remaining occurrences last.
              </Content>
            )}
            {!pricesLoading && pricesError && (
              // A warm refresh failure keeps the previously loaded (now possibly stale) prices on
              // screen, so the wording distinguishes the two cases.
              <Content component="small">
                {catalogProducts.length === 0 ? 'Pricing could not be loaded' : 'Pricing could not be refreshed'}:{' '}
                {pricesError}
              </Content>
            )}
            {backupUsageNote !== null && <Content component="small">{backupUsageNote}</Content>}
            {!totalLoading && !pricesError && totalCost === null && (
              <Content component="small">
                The total needs a price for every resource, and a non-zero allocation for vCPUs,
                memory and disk.
              </Content>
            )}
            {vm.replica_of !== null && (
              <Content component="small">
                This virtual machine is a DRP replica — it is not billed; recurring costs are carried by the
                primary.
              </Content>
            )}
            {/* Enrollment is a staff-internal mechanism (VmBillingCard is staff-only), so this
                counter-signal is staff-only too: for tenants the total is their real recurring
                cost whether it's invoiced by the meter or outside the framework. Same gate as
                VmBillingCard: is_ancalagon_staff alone, never combined with !isImpersonating. */}
            {user?.is_ancalagon_staff === true &&
              vm.replica_of === null &&
              vm.billing &&
              !vm.billing.enrolled && (
                <Content component="small">
                  Not enrolled in monthly billing — the total shows the cost at current catalog prices, not an
                  active invoice.
                </Content>
              )}
          </CardBody>
        </Card>
      </GridItem>
      <GridItem span={12}>
        <Card>
          <CardTitle>Network</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Primary IPv4</DescriptionListTerm>
                <DescriptionListDescription>{vm.primary_ip4?.address ?? 'None'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Primary IPv6</DescriptionListTerm>
                <DescriptionListDescription>{vm.primary_ip6?.address ?? 'None'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Site</DescriptionListTerm>
                <DescriptionListDescription>{vm.site?.name ?? 'Unassigned'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Cluster</DescriptionListTerm>
                <DescriptionListDescription>{vm.cluster?.name ?? 'Unassigned'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Host Device</DescriptionListTerm>
                <DescriptionListDescription>{vm.device?.name ?? 'Unassigned'}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
      {vm.protected && vm.replica && (
        <GridItem span={12}>
          <Card>
            <CardTitle>DRP Protection</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <DescriptionListGroup>
                  <DescriptionListTerm>Protection state</DescriptionListTerm>
                  <DescriptionListDescription>
                    {vm.replica.status === 'active' ? (
                      <Label status="warning" icon={<ShieldAltIcon />}>
                        Running on backup
                      </Label>
                    ) : (
                      <Label color="green" icon={<ShieldAltIcon />}>
                        Protected
                      </Label>
                    )}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Replica status</DescriptionListTerm>
                  <DescriptionListDescription>{replicaStatusText(vm.replica.status)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Backup hypervisor</DescriptionListTerm>
                  <DescriptionListDescription>{vm.replica.device?.name ?? '\u2014'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Site</DescriptionListTerm>
                  <DescriptionListDescription>{vm.replica.site?.name ?? '\u2014'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Cluster</DescriptionListTerm>
                  <DescriptionListDescription>{vm.replica.cluster?.name ?? '\u2014'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Replica libvirt UUID</DescriptionListTerm>
                  <DescriptionListDescription>
                    {vm.replica.libvirt_uuid ? (
                      <span
                        style={{
                          fontFamily: 'var(--pf-t--global--font--family--mono)',
                          color: 'var(--pf-t--global--text--color--subtle)',
                        }}
                      >
                        {vm.replica.libvirt_uuid}
                      </span>
                    ) : (
                      '\u2014'
                    )}
                  </DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
            </CardBody>
          </Card>
        </GridItem>
      )}
      <VmBillingCard vm={vm} onVmUpdate={onVmUpdate} />
      {isDealModalOpen && dealClientId !== null && vm.id !== null && (
        <SpecialDealModal
          isOpen
          onClose={() => setIsDealModalOpen(false)}
          onSaved={() => void refetchAdminSpecials()}
          clientId={dealClientId}
          clientName={target?.name ?? null}
          attachment={{ parentType: SPECIAL_DEAL_PARENT_VM, parentId: vm.id, parentName: vm.name }}
        />
      )}
      {editDeal && dealClientId !== null && vm.id !== null && (
        <SpecialDealModal
          key={editDeal.id}
          isOpen
          deal={editDeal}
          onClose={() => setEditDeal(null)}
          onSaved={() => void refetchAdminSpecials()}
          clientId={dealClientId}
          clientName={target?.name ?? null}
          attachment={{ parentType: SPECIAL_DEAL_PARENT_VM, parentId: vm.id, parentName: vm.name }}
        />
      )}
    </Grid>
  );
};

export default VmOverviewTab;
